/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * BuilderCanvas — the main sortable list of sections in the builder.
 * Uses @dnd-kit/sortable for drag-to-reorder.
 */

import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SectionCard from './SectionCard';
import { useTheme } from '../../theme/ThemeContext';

export default function BuilderCanvas({
  sections,
  dispatch,
  selectedClientId,
  onSelectSection,
  onAddSection,
  onRemoveSection,
  appId,
  pageId,
}) {
  const { theme } = useTheme();
  const [activeId, setActiveId] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const fromIndex = sections.findIndex(s => s._clientId === active.id);
    const toIndex = sections.findIndex(s => s._clientId === over.id);
    dispatch({ type: 'REORDER_SECTIONS', fromIndex, toIndex });
  };

  const activeSection = sections.find(s => s._clientId === activeId);

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map(s => s._clientId)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map(section => (
            <SectionCard
              key={section._clientId}
              section={section}
              isSelected={selectedClientId === section._clientId}
              onEdit={() => onSelectSection && onSelectSection(section._clientId)}
              onDelete={() => {
                if (onRemoveSection) onRemoveSection(section._clientId);
                else dispatch({ type: 'REMOVE_SECTION', clientId: section._clientId });
              }}
              appId={appId}
              pageId={pageId}
            />
          ))}
        </SortableContext>

        <DragOverlay>
          {activeSection && (
            <div
              style={{
                background: '#fff',
                border: `2px solid ${theme.primary}`,
                borderRadius: 8,
                padding: '10px 16px',
                opacity: 0.9,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                fontWeight: 600,
                fontSize: 13,
                color: theme.primary,
              }}
            >
              <i className="bi bi-grip-vertical me-2" />
              {activeSection.section_type.replace(/_/g, ' ')}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {sections.length === 0 && (
        <div
          className="text-center py-5 rounded"
          style={{
            border: `2px dashed ${theme.primary}40`,
            color: '#94a3b8',
            marginTop: 16,
          }}
        >
          <i className="bi bi-plus-circle" style={{ fontSize: 36, opacity: 0.4 }} />
          <div className="fw-semibold mt-2">Add your first section</div>
          <div className="small mt-1">Click a section type in the left panel to get started</div>
        </div>
      )}
    </div>
  );
}
